"use client";

import React, { useEffect, useState, useRef } from "react";
import {
  Line,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";

// Helper function to format Indian numbers
const formatIndianNumber = (num) => {
  if (num === undefined || num === null) return '';
  num = Number(num);
  if (Math.abs(num) >= 1e7) return (num / 1e7).toFixed(2) + ' Cr';
  if (Math.abs(num) >= 1e5) return (num / 1e5).toFixed(2) + ' L';
  return num.toLocaleString('en-IN');
};

// Color select dropdown component
function ColorSelect({ value, onChange, strategies, strategyColors }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <div
        className="flex items-center gap-2 px-2 py-1 bg-black border border-gray-700 rounded cursor-pointer min-w-[200px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {value !== "All" && (
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: strategyColors[value] }}
          />
        )}
        <span>{value === "All" ? "All Strategies" : value}</span>
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-black border border-gray-700 rounded shadow-lg max-h-[400px] overflow-y-auto">
          <div
            className="px-2 py-1 cursor-pointer hover:bg-gray-800"
            onClick={() => {
              onChange("All");
              setIsOpen(false);
            }}
          >
            All Strategies
          </div>
          {strategies.map(strategy => (
            <div
              key={strategy}
              className="px-2 py-1 cursor-pointer hover:bg-gray-800 flex items-center gap-2"
              onClick={() => {
                onChange(strategy);
                setIsOpen(false);
              }}
            >
              <span
                className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: strategyColors[strategy] }}
              />
              <span>{strategy}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Generate a color for a strategy
const generateColor = (index) => {
  const colors = [
    "#1f77b4", // Blue
    "#ff7f0e", // Orange
    "#2ca02c", // Green
    "#d62728", // Red
    "#9467bd", // Purple
    "#8c564b", // Brown
    "#e377c2", // Pink
    "#7f7f7f", // Gray
    "#bcbd22", // Yellow-green
    "#17becf", // Cyan
  ];
  return colors[index % colors.length];
};

const chartConfig = {
  line: {
    strokeWidth: 2,
    dot: false,
    connectNulls: true,
  },
};

export function StrategyCumulativePNLChart({ data = {} }) {
  const [timeRange, setTimeRange] = useState("all");
  const [filteredData, setFilteredData] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [strategyColors, setStrategyColors] = useState({});
  const [selectedStrategy, setSelectedStrategy] = useState("All");

  // Process data and extract strategies
  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      setFilteredData([]);
      setStrategies([]);
      setStrategyColors({});
      return;
    }

    // Get all strategy names from the object keys
    const strategyNames = Object.keys(data);

    // Create color mapping for strategies
    const colors = {};
    strategyNames.forEach((strategy, index) => {
      colors[strategy] = generateColor(index);
    });

    setStrategies(strategyNames);
    setStrategyColors(colors);

    // Transform data into the format needed for the chart
    const transformedData = transformData(data);
    setFilteredData(transformedData);
  }, [data]);

  // Transform the data from strategy-keyed object to array of date-keyed objects
  const transformData = (rawData) => {
    if (!rawData || Object.keys(rawData).length === 0) return [];

    // Get all unique dates across all strategies
    const allDates = new Set();
    Object.values(rawData).forEach(strategyData => {
      if (Array.isArray(strategyData)) {
        strategyData.forEach(item => {
          if (item && item.date) {
            allDates.add(item.date);
          }
        });
      }
    });

    // Create an array of objects with date and PNL values for each strategy
    return Array.from(allDates).map(date => {
      const dataPoint = { date };
      Object.entries(rawData).forEach(([strategy, strategyData]) => {
        if (Array.isArray(strategyData)) {
          const dayData = strategyData.find(item => item && item.date === date);
          dataPoint[strategy] = dayData ? dayData.cumulative_pnl : null;
        }
      });
      return dataPoint;
    }).sort((a, b) => {
      // Sort by date (DD-MM-YYYY format)
      const [aDay, aMonth, aYear] = a.date.split("-");
      const [bDay, bMonth, bYear] = b.date.split("-");
      return new Date(aYear, aMonth - 1, aDay) - new Date(bYear, bMonth - 1, bDay);
    });
  };

  // Filtering logic for time range
  useEffect(() => {
    if (!data || Object.keys(data).length === 0) {
      setFilteredData([]);
      return;
    }
    
    const transformedData = transformData(data);
    let filtered = [...transformedData];

    if (timeRange !== "all") {
      const now = new Date();
      filtered = filtered.filter(d => {
        if (!d || !d.date) return false;
        const [day, month, year] = d.date.split("-");
        const date = new Date(year, month - 1, day);
        let days = 0;
        if (timeRange === "7d") days = 7;
        else if (timeRange === "15d") days = 15;
        else if (timeRange === "30d") days = 30;
        return now - date < days * 24 * 60 * 60 * 1000;
      });
    }
    setFilteredData(filtered);
  }, [timeRange, data]);

  // Get strategies to display based on selection
  const getDisplayStrategies = () => {
    if (selectedStrategy === "All") {
      return strategies;
    }
    return [selectedStrategy];
  };

  // Custom tooltip content component
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div className="bg-black border border-gray-700 rounded p-3 shadow-lg">
        <p className="text-gray-300 mb-2">
          {label ? (() => {
            const [day, month, year] = label.split("-");
            const dateObj = new Date(`${year}-${month}-${day}`);
            return dateObj.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric"
            });
          })() : ""}
        </p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.stroke }}
              />
              <span className="text-gray-300">{entry.name}:</span>
              <span className="text-white font-medium">
                {formatIndianNumber(entry.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // If no data is available, show a message
  if (!data || Object.keys(data).length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Strategy-wise Cumulative PNL</CardTitle>
          <CardDescription>Cumulative PNL trends for individual strategies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[450px] w-full flex items-center justify-center">
            <p className="text-muted-foreground">No data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Strategy-wise Cumulative PNL</CardTitle>
            <CardDescription>
              {selectedStrategy === "All"
                ? `Showing Cumulative PNL for all strategies`
                : `Showing Cumulative PNL for ${selectedStrategy}`}
            </CardDescription>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4">
              {getDisplayStrategies().map(strategy => (
                <div key={strategy} className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: strategyColors[strategy] }}
                  />
                  <span className="text-sm text-gray-300">{strategy}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-row items-center gap-4 ml-4">
            {/* Custom strategy selector with color indicators */}
            <ColorSelect
              value={selectedStrategy}
              onChange={setSelectedStrategy}
              strategies={strategies}
              strategyColors={strategyColors}
            />
            {/* Time range selector */}
            <select
              value={timeRange}
              onChange={e => setTimeRange(e.target.value)}
              className="bg-black text-white border border-gray-700 rounded px-2 py-1"
            >
              <option value="all">ALL</option>
              <option value="7d">7 days</option>
              <option value="15d">15 days</option>
              <option value="30d">30 days</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredData.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No data available for this selection.</div>
        ) : (
          <ChartContainer className="h-[450px] w-full" config={chartConfig}>
            <LineChart data={filteredData} margin={{ left: 60, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                tickFormatter={(value) => {
                  if (!value) return "";
                  const [day, month, year] = value.split("-");
                  const dateObj = new Date(`${year}-${month}-${day}`);
                  return dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
                }}
                interval={Math.ceil(filteredData.length / 8)}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={1}
                tickCount={11}
                domain={["dataMin-200000", "dataMax+100000"]}
                tickFormatter={formatIndianNumber}
              />
              <Tooltip
                content={<CustomTooltip />}
              />
              {getDisplayStrategies().map(strategy => (
                <Line
                  key={strategy}
                  type="monotone"
                  dataKey={strategy}
                  stroke={strategyColors[strategy]}
                  strokeWidth={2}
                  dot={false}
                  name={strategy}
                  connectNulls={true}
                />
              ))}
            </LineChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
} 